import React, { useState, useEffect, useCallback } from 'react';
import { paymentsAPI } from '../services/api';
import { 
  Search, Filter, Download, RefreshCw, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, Clock, Eye, RotateCcw, X
} from 'lucide-react';

const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({ search: '', status: '', dateFrom: '', dateTo: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refunding, setRefunding] = useState(false);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await paymentsAPI.list({ ...filters, page: pagination.page, page_size: pagination.pageSize });
      setPayments(res.data.payments || []);
      setPagination(prev => ({ ...prev, total: res.data.total }));
    } catch (err) {
      console.error('Payments error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.pageSize]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const handleExport = async () => {
    try {
      const res = await paymentsAPI.export(filters);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payments_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const handleRefund = async () => {
    if (!showRefundModal) return;
    setRefunding(true);
    try {
      await paymentsAPI.refund(showRefundModal.id, { amount: parseFloat(refundAmount) || showRefundModal.amount });
      setShowRefundModal(null);
      setRefundAmount('');
      fetchPayments();
    } catch (err) {
      alert(err.response?.data?.detail || 'Ошибка возврата');
    } finally {
      setRefunding(false);
    }
  };

  const formatAmount = (amount, currency = 'KZT') => {
    if (currency === 'KZT') return `${(amount || 0).toLocaleString('ru-RU')} ₸`;
    return `${((amount || 0) / 100).toFixed(2)} ${currency}`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-emerald-500/20 text-emerald-400',
      declined: 'bg-red-500/20 text-red-400',
      failed: 'bg-red-500/20 text-red-400',
      pending: 'bg-amber-500/20 text-amber-400',
      refunded: 'bg-purple-500/20 text-purple-400'
    };
    const labels = { approved: 'Успешно', declined: 'Отклонён', failed: 'Ошибка', pending: 'В обработке', refunded: 'Возврат' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-slate-500/20 text-slate-400'}`}>{labels[status] || status}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Платежи</h1>
          <p className="text-slate-400 mt-1">Всего: {pagination.total.toLocaleString('ru-RU')}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExport} className="btn-secondary flex items-center gap-2">
            <Download className="w-4 h-4" /> Экспорт
          </button>
          <button onClick={fetchPayments} className="btn-secondary">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              placeholder="Поиск по ID, order_id..."
              className="input w-full pl-10"
            />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className={`btn-secondary flex items-center gap-2 ${showFilters ? 'bg-emerald-500/20 text-emerald-400' : ''}`}>
            <Filter className="w-4 h-4" /> Фильтры
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-700">
            <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className="input">
              <option value="">Все статусы</option>
              <option value="approved">Успешные</option>
              <option value="declined">Отклонённые</option>
              <option value="pending">В обработке</option>
              <option value="refunded">Возвраты</option>
            </select>
            <input type="date" value={filters.dateFrom} onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} className="input" placeholder="От" />
            <input type="date" value={filters.dateTo} onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} className="input" placeholder="До" />
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left p-4 text-sm font-medium text-slate-400">ID</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-400">Order ID</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-400">Сумма</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-400">Статус</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-400">Способ</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-400">Дата</th>
                  <th className="text-right p-4 text-sm font-medium text-slate-400">Действия</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                    <td className="p-4 text-white font-mono text-sm">#{p.id}</td>
                    <td className="p-4 text-slate-300 text-sm">{p.order_id || '—'}</td>
                    <td className="p-4 text-white font-medium">{formatAmount(p.amount, p.currency)}</td>
                    <td className="p-4">{getStatusBadge(p.status)}</td>
                    <td className="p-4 text-slate-300 text-sm">{p.payment_method || 'Карта'}</td>
                    <td className="p-4 text-slate-400 text-sm">{new Date(p.created_at).toLocaleString('ru-RU')}</td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => setSelectedPayment(p)} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg" title="Детали">
                          <Eye className="w-4 h-4" />
                        </button>
                        {p.status === 'approved' && (
                          <button onClick={() => { setShowRefundModal(p); setRefundAmount(String(p.amount)); }} className="p-2 text-slate-400 hover:text-amber-400 hover:bg-amber-500/20 rounded-lg" title="Возврат">
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {payments.length === 0 && (
                  <tr><td colSpan={7} className="p-8 text-center text-slate-400">Платежей не найдено</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {Math.ceil(pagination.total / pagination.pageSize) > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-slate-700">
            <span className="text-sm text-slate-400">Страница {pagination.page} из {Math.ceil(pagination.total / pagination.pageSize)}</span>
            <div className="flex gap-2">
              <button onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page <= 1} className="btn-secondary p-2 disabled:opacity-50">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page >= Math.ceil(pagination.total / pagination.pageSize)} className="btn-secondary p-2 disabled:opacity-50">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Payment Detail Modal */}
      {selectedPayment && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedPayment(null)}>
          <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Платёж #{selectedPayment.id}</h3>
              <button onClick={() => setSelectedPayment(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Сумма:</span><span className="text-white font-medium">{formatAmount(selectedPayment.amount, selectedPayment.currency)}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Статус:</span>{getStatusBadge(selectedPayment.status)}</div>
              <div className="flex justify-between"><span className="text-slate-400">Order ID:</span><span className="text-white">{selectedPayment.order_id || '—'}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Способ оплаты:</span><span className="text-white">{selectedPayment.payment_method || 'Карта'}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Карта:</span><span className="text-white font-mono">{selectedPayment.card_mask || '****'}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Email клиента:</span><span className="text-white">{selectedPayment.customer_email || '—'}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Дата:</span><span className="text-white">{new Date(selectedPayment.created_at).toLocaleString('ru-RU')}</span></div>
              {selectedPayment.decline_reason && <div className="mt-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">Причина отклонения: {selectedPayment.decline_reason}</div>}
            </div>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Возврат платежа #{showRefundModal.id}</h3>
            <p className="text-slate-400 text-sm mb-4">Максимальная сумма: {formatAmount(showRefundModal.amount, showRefundModal.currency)}</p>
            <input type="number" value={refundAmount} onChange={e => setRefundAmount(e.target.value)} max={showRefundModal.amount} className="input w-full mb-4" placeholder="Сумма возврата" />
            <div className="flex gap-2">
              <button onClick={() => setShowRefundModal(null)} className="btn-secondary flex-1">Отмена</button>
              <button onClick={handleRefund} disabled={refunding} className="btn-primary flex-1 bg-amber-500 hover:bg-amber-600">
                {refunding ? 'Обработка...' : 'Вернуть'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsPage;
